// uploadFragmentPhotos — sequential multi-photo upload for a fragment.
//
// Stage 10: each photo gets a crypto.randomUUID() filename so future
// editing can reorder/delete individual photos without path collision.
//
// Path: fragments-photos/{fragmentId}/{uuid}.jpg
//
// Returns the list of download URLs in input order. On any photo
// failure, throws — caller is responsible for cleanup (delete the
// partial uploads + fragment doc).

import { getFirebase } from "@goodbye/firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { resizeImageToBlob } from "./resizeImageToBlob";

export interface UploadFragmentPhotosInput {
  fragmentId: string;
  files: File[];
}

export interface UploadFragmentPhotosResult {
  photoUrls: string[];
  /** Storage paths in same order, for cleanup-on-fail. */
  photoStoragePaths: string[];
}

export async function uploadFragmentPhotos(
  input: UploadFragmentPhotosInput,
): Promise<UploadFragmentPhotosResult> {
  const { fragmentId, files } = input;
  const storage = getFirebase().storage;
  const photoUrls: string[] = [];
  const photoStoragePaths: string[] = [];

  for (const file of files) {
    const uuid =
      typeof crypto?.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const path = `fragments-photos/${fragmentId}/${uuid}.jpg`;
    // 2026-05-30:碎片是給學員留念的內容,盡量不壓 — 放寬到 2048px / 0.92
    // (預設 1024 / 0.78 留給線索照、訊息照)。
    const blob = await resizeImageToBlob(file, { maxDim: 2048, quality: 0.92 });
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, blob, { contentType: "image/jpeg" });
    const url = await getDownloadURL(storageRef);
    photoUrls.push(url);
    photoStoragePaths.push(path);
  }

  return { photoUrls, photoStoragePaths };
}
